import type {
  Campaign,
  CampaignReference,
  CampaignReferences,
  CampaignScope,
  CampaignWithReference,
  Group,
  YammerState,
} from '../../state/types';
import { findById as findCampaignCoverImage } from '../campaignCoverImage/selectors';
import { findCampaignById } from '../campaigns/selectors';
import { createFindById, createFindByIdThrows } from '../findById';
import type { FindById, FindByIdThrows } from '../findById';

/**
 * @deprecated PERFORMANCE This returns an entity object and forces unnecessary re-renders.
 * This should be replaced by a `getCampaignReferenceField` selector.
 */
export const findCampaignReferenceById: FindById<'campaignReferences'> = createFindById('campaignReferences');
/**
 * @deprecated PERFORMANCE This returns an entity object and forces unnecessary re-renders.
 * This should be replaced by a `getCampaignReferenceFieldThrows` selector.
 */
export const findCampaignReferenceByIdThrows: FindByIdThrows<'campaignReferences'> =
  createFindByIdThrows('campaignReferences');

type GetCampaignTelemetryId = (
  state: YammerState,
  campaignId: Campaign['id']
) => CampaignReference['telemetryId'] | undefined;
export const getCampaignTelemetryId: GetCampaignTelemetryId = (state, campaignId) =>
  findCampaignReferenceById(state, campaignId)?.telemetryId;

type GetCampaignReferencesState = (state: YammerState) => CampaignReferences;
export const getCampaignReferencesState: GetCampaignReferencesState = (state) => state.campaignReferences;

type GetFulfilledCampaignReferences = (state: YammerState) => Record<string, CampaignReference>;
/**
 * @deprecated PERFORMANCE This returns an object containing ALL fulfilled campaign references, this will return
 * a new object instance every time ANY campaign reference is updated across the entire system. This selector
 * should be deleted.
 */
export const getFulfilledCampaignReferences: GetFulfilledCampaignReferences = (state) =>
  state.campaignReferences.fulfilled.byId;

type GetCampaignWithReference = (state: YammerState, campaignId: Campaign['id']) => CampaignWithReference | undefined;
/**
 * @deprecated PERFORMANCE This returns a brand new object reference on every call! This selector must at the very least
 * be a cached selector, however it should really be deleted and consumers should be responsible for passing the campaign
 * id to a `useCampaignField` selector.
 */
export const getCampaignWithReference: GetCampaignWithReference = (state, campaignId) => {
  const campaignReference = findCampaignReferenceById(state, campaignId);
  const campaign = findCampaignById(state, campaignId);
  const campaignCoverImage = findCampaignCoverImage(state, campaignId);

  if (!campaignReference || !campaign || !campaignCoverImage) {
    return undefined;
  }

  const campaignWithReference = {
    ...campaign,
    ...campaignReference,
    ...campaignCoverImage,
  };

  return campaignWithReference;
};

type IsMTOCampaign = (state: YammerState, campaignId: Campaign['id'] | undefined) => boolean;
export const isMTOCampaign: IsMTOCampaign = (state, campaignId) => {
  if (campaignId === undefined) {
    return false;
  }

  return getCampaignScope(state, campaignId)?.type === 'MULTI_TENANT_ORGANIZATION';
};

type IsGroupCampaign = (state: YammerState, campaignId: Campaign['id'] | undefined) => boolean;
export const isGroupCampaign: IsGroupCampaign = (state, campaignId) => {
  if (campaignId === undefined) {
    return false;
  }

  return getCampaignScope(state, campaignId)?.type === 'GROUP';
};

type GetGroupIdForCampaign = (state: YammerState, campaignId: Group['id']) => string | null;
export const getGroupIdForCampaign: GetGroupIdForCampaign = (state, campaignId) => {
  const scope = getCampaignScope(state, campaignId);

  return scope?.type === 'GROUP' ? scope.groupId : null;
};

type GetGroupIdForCampaignThrows = (state: YammerState, campaignId: Group['id']) => string;
export const getGroupIdForCampaignThrows: GetGroupIdForCampaignThrows = (state, campaignId) => {
  const scope = getCampaignScope(state, campaignId);

  if (scope?.type !== 'GROUP') {
    throw new Error(`Campaign ${campaignId} is not a group campaign.`);
  }

  return scope.groupId;
};

type GetCampaignScope = (state: YammerState, campaignId: Campaign['id']) => CampaignScope | undefined;
export const getCampaignScope: GetCampaignScope = (state, campaignId) =>
  findCampaignReferenceById(state, campaignId)?.scope;
